import React, { useState } from 'react';
import { cn } from 'lib/utils';
import { useDebug } from 'contexts/DebugContext';
import { Button } from 'components/ui/button';

type DebugPanelProps = {
  title?: string;
  className?: string;
  children: React.ReactNode;
};

const formatValue = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'undefined') {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
};

type DebugFieldProps = {
  label: string;
  value: unknown;
};

export const DebugField: React.FC<DebugFieldProps> = ({ label, value }) => (
  <div className="flex flex-wrap items-start justify-between gap-2">
    <span className="font-medium text-orange-700">{label}</span>
    <span className="min-w-0 flex-1 whitespace-pre-wrap break-all font-mono text-orange-900 text-right">
      {formatValue(value)}
    </span>
  </div>
);

export const DebugPanel: React.FC<DebugPanelProps> = ({ title, className, children }) => {
  const { isEnabled, canToggle, setEnabled } = useDebug();
  const [isDismissed, setIsDismissed] = useState(false);

  if (!isEnabled || isDismissed) {
    return null;
  }

  return (
    <div
      className={cn(
        'max-w-full rounded-md border border-dashed border-orange-500/70 bg-orange-500/10 p-3 text-[11px] leading-tight text-orange-950 shadow-lg backdrop-blur-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold uppercase tracking-wide text-orange-600">
          {title || 'Debug'}
        </div>
        <Button
          type="button"
          variant="outline"
          size="auto"
          className="min-h-8 border-orange-700 px-3 py-1 text-xs text-orange-900 hover:bg-orange-500/10"
          aria-label="Turn off debug panel"
          onClick={() => {
            setIsDismissed(true);
            if (canToggle) {
              void setEnabled(false).catch((error) => {
                console.error('DebugPanel: failed to save debug mode preference', error);
              });
            }
          }}
        >
          Off
        </Button>
      </div>
      <div className="mt-2 space-y-2 break-words">
        {children}
      </div>
    </div>
  );
};
